import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import type { Schemas } from "@/types/api";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  list: (params?: Record<string, string>) => ["melhoria", "list", params] as const,
  detail: (id: string) => ["melhoria", "detail", id] as const,
};

// ---------------------------------------------------------------------------
// Payload types (no id/timestamps — just what the API accepts)
// ---------------------------------------------------------------------------

export type AcaoPayload = Schemas["MelhoriaAcaoCreate"];

export type RegistoCreatePayload = Schemas["RegistoMelhoriaCreate"];

export interface RegistoUpdatePayload extends Omit<RegistoCreatePayload, "acoes"> {
  acoes?: AcaoPayload[] | null;
}

// ---------------------------------------------------------------------------
// List & detail
// ---------------------------------------------------------------------------

export const useMelhoria = (filters?: {
  folha?: string;
  tipo_constatacao?: string;
  estado?: string;
  ano?: number;
}) => {
  const params: Record<string, string> = {};
  if (filters?.folha) params.folha = filters.folha;
  if (filters?.tipo_constatacao) params.tipo_constatacao = filters.tipo_constatacao;
  if (filters?.estado) params.estado = filters.estado;
  if (filters?.ano) params.ano = String(filters.ano);

  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => api.get("/energia/melhoria", { query: params }),
  });
};

// ---------------------------------------------------------------------------
// Registo mutations
// ---------------------------------------------------------------------------

export const useCreateMelhoria = () => {
  return useMutationWithToast({
    mutationFn: (data: RegistoCreatePayload) =>
      api.post("/energia/melhoria", { body: data }),
    invalidateQueries: [["melhoria"]],
  });
};

export const useUpdateMelhoria = () => {
  return useMutationWithToast({
    mutationFn: ({ id, ...data }: RegistoUpdatePayload & { id: string }) =>
      api.patch("/energia/melhoria/{registo_id}", { path: { registo_id: id }, body: data }),
    invalidateQueries: [["melhoria"]],
  });
};

export const useDeleteMelhoria = () => {
  return useMutationWithToast({
    mutationFn: (id: string) => api.delete("/energia/melhoria/{registo_id}", { path: { registo_id: id } }),
    invalidateQueries: [["melhoria"]],
  });
};

